'use client';

import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * useDropdown - Shared dropdown behavior hook
 *
 * Provides common functionality for dropdown components:
 * - Open/close state management
 * - Outside click detection
 * - Keyboard navigation (arrows, Enter, Escape, Home, End)
 * - Focus index management with auto-scroll
 * - ARIA helper props
 *
 * @param {Object} options
 * @param {Array} options.items - Array of selectable items
 * @param {Function} options.onSelect - Callback when item is selected
 * @param {*} options.selectedValue - Currently selected value (for initial focus)
 * @param {Function} options.getItemValue - Function to extract value from item (default: item => item.key || item.value || item.id)
 * @param {Function} options.isItemSelectable - Function to determine if item is selectable (default: () => true). Non-selectable items are skipped during keyboard navigation.
 * @param {string} options.identifier - Unique identifier for ARIA attributes
 * @param {boolean} options.preventOutsideClickPropagation - If true, prevents outside clicks from propagating when dropdown is open
 */
export default function useDropdown({
  items = [],
  onSelect,
  selectedValue,
  getItemValue = item => item.key ?? item.value ?? item.id,
  isItemSelectable = () => true,
  identifier = 'dropdown',
  preventOutsideClickPropagation = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef(null);
  const itemRefs = useRef([]);

  // Find index of currently selected item (or first selectable)
  const findSelectedIndex = useCallback(() => {
    if (selectedValue === undefined || selectedValue === null) {
      // Return first selectable item
      for (let i = 0; i < items.length; i++) {
        if (isItemSelectable(items[i])) return i;
      }
      return 0;
    }
    const index = items.findIndex(item_0 => getItemValue(item_0) === selectedValue);
    return index >= 0 ? index : 0;
  }, [items, selectedValue, getItemValue, isItemSelectable]);

  // Open dropdown
  const open = useCallback(() => {
    setIsOpen(true);
    setFocusedIndex(findSelectedIndex());
  }, [findSelectedIndex]);

  // Close dropdown
  const close = useCallback(() => {
    setIsOpen(false);
    setFocusedIndex(-1);
  }, []);

  // Toggle dropdown
  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, open, close]);

  // Select an item
  const selectItem = useCallback((item_1, index_0) => {
    if (onSelect) {
      onSelect(item_1, index_0);
    }
    close();
  }, [onSelect, close]);

  // Handle outside clicks
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        if (preventOutsideClickPropagation) {
          event.preventDefault();
          event.stopPropagation();
        }
        close();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, close, preventOutsideClickPropagation]);

  // Auto-scroll focused item into view
  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
      itemRefs.current[focusedIndex].scrollIntoView({
        block: 'nearest'
      });
    }
  }, [focusedIndex, isOpen]);

  // Find next selectable index in given direction
  const findNextSelectableIndex = useCallback((fromIndex, direction) => {
    let index_1 = fromIndex + direction;
    while (index_1 >= 0 && index_1 < items.length) {
      if (isItemSelectable(items[index_1])) {
        return index_1;
      }
      index_1 += direction;
    }
    return fromIndex; // Stay at current if no selectable found
  }, [items, isItemSelectable]);

  // Find first/last selectable index
  const findFirstSelectableIndex = useCallback(() => {
    for (let i_0 = 0; i_0 < items.length; i_0++) {
      if (isItemSelectable(items[i_0])) return i_0;
    }
    return 0;
  }, [items, isItemSelectable]);
  const findLastSelectableIndex = useCallback(() => {
    for (let i_1 = items.length - 1; i_1 >= 0; i_1--) {
      if (isItemSelectable(items[i_1])) return i_1;
    }
    return items.length - 1;
  }, [items, isItemSelectable]);

  // Keyboard navigation handler
  const handleKeyDown = useCallback(event_0 => {
    // Open on Enter, Space, or Arrow keys when closed
    if (!isOpen && ['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event_0.key)) {
      event_0.preventDefault();
      open();
      return;
    }
    if (!isOpen) return;
    switch (event_0.key) {
      case 'ArrowDown':
        event_0.preventDefault();
        setFocusedIndex(prev_0 => findNextSelectableIndex(prev_0, 1));
        break;
      case 'ArrowUp':
        event_0.preventDefault();
        setFocusedIndex(prev => findNextSelectableIndex(prev, -1));
        break;
      case 'Enter':
      case ' ':
        event_0.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < items.length && isItemSelectable(items[focusedIndex])) {
          selectItem(items[focusedIndex], focusedIndex);
        }
        break;
      case 'Escape':
        event_0.preventDefault();
        close();
        break;
      case 'Home':
        event_0.preventDefault();
        setFocusedIndex(findFirstSelectableIndex());
        break;
      case 'End':
        event_0.preventDefault();
        setFocusedIndex(findLastSelectableIndex());
        break;
      case 'Tab':
        // Allow tab to close and move focus naturally
        close();
        break;
    }
  }, [isOpen, items, focusedIndex, open, close, selectItem, isItemSelectable, findNextSelectableIndex, findFirstSelectableIndex, findLastSelectableIndex]);

  // Register item ref for auto-scroll
  const registerItemRef = useCallback(index_2 => el => {
    itemRefs.current[index_2] = el;
  }, []);

  // ARIA props for the trigger button
  const getTriggerProps = useCallback(() => ({
    'aria-haspopup': 'listbox',
    'aria-expanded': isOpen,
    'aria-controls': identifier,
    'aria-activedescendant': isOpen && focusedIndex >= 0 ? `${identifier}-option-${focusedIndex}` : undefined,
    onClick: toggle,
    onKeyDown: handleKeyDown
  }), [isOpen, identifier, focusedIndex, toggle, handleKeyDown]);

  // ARIA props for the listbox
  const getListProps = useCallback(() => ({
    id: identifier,
    role: 'listbox',
    'aria-labelledby': `${identifier}-label`
  }), [identifier]);

  // Props for each option
  const getItemProps = useCallback((item_2, index_3) => ({
    id: `${identifier}-option-${index_3}`,
    role: 'option',
    'aria-selected': getItemValue(item_2) === selectedValue,
    ref: registerItemRef(index_3),
    onClick: () => selectItem(item_2, index_3),
    tabIndex: -1
  }), [identifier, selectedValue, getItemValue, registerItemRef, selectItem]);
  return {
    // State
    isOpen,
    focusedIndex,
    // Refs
    containerRef,
    // Actions
    open,
    close,
    toggle,
    selectItem,
    // Event handlers
    handleKeyDown,
    // ARIA prop getters
    getTriggerProps,
    getListProps,
    getItemProps
  };
}